import React from 'react';
import { formatNum, CHAIN_LABELS, getNativeSymbol } from '../../utils/masterChefUtils';

const CHAIN_PILL = {
  solana: 'border-purple-200/80 dark:border-purple-800/60 bg-purple-50/80 dark:bg-purple-950/30',
  base: 'border-blue-200/80 dark:border-blue-800/60 bg-blue-50/80 dark:bg-blue-950/30',
  ethereum: 'border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50/80 dark:bg-indigo-950/30',
  bsc: 'border-amber-200/80 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-950/30',
};

const CHAIN_TEXT = {
  solana: 'text-purple-800 dark:text-purple-300',
  base: 'text-blue-800 dark:text-blue-300',
  ethereum: 'text-indigo-800 dark:text-indigo-300',
  bsc: 'text-amber-800 dark:text-amber-300',
};

function chainActivity(s) {
  return (s?.active || 0) + (s?.closed || 0);
}

function ChainBreakdown({ byChain }) {
  if (!byChain || Object.keys(byChain).length === 0) return null;

  const chains = Object.entries(byChain)
    .filter(([, s]) => chainActivity(s) > 0)
    .sort(
      (a, b) => (b[1].active || 0) - (a[1].active || 0) || (b[1].closed || 0) - (a[1].closed || 0),
    );

  if (!chains.length) return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/80">
      <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
        Per chain · native units
      </p>
      <div className="space-y-1.5">
        {chains.map(([chain, s]) => {
          const sym = getNativeSymbol(chain);
          const pnl = s.totalPnl || 0;
          const pnlPct = s.totalPnlPercent || 0;
          const positive = pnl >= 0;
          const pill = CHAIN_PILL[chain] || 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40';
          const text = CHAIN_TEXT[chain] || 'text-gray-800 dark:text-gray-200';

          return (
            <div
              key={chain}
              className={`rounded border px-2.5 py-1.5 ${pill}`}
            >
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className={`font-bold ${text}`}>{CHAIN_LABELS[chain] || chain.toUpperCase()}</span>
                <span className="text-gray-500 dark:text-gray-400 shrink-0">
                  {s.active || 0} open · {s.closed || 0} closed
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5 text-[11px]">
                <span className="text-gray-600 dark:text-gray-300">
                  In <span className="font-medium text-gray-900 dark:text-white">{formatNum(s.totalInvested, 3)}</span>
                  {' '}{sym}
                </span>
                <span className={`font-semibold shrink-0 ${positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  P&L {positive ? '+' : ''}{formatNum(pnl, 3)} {sym}
                  <span className="font-normal opacity-80"> ({positive ? '+' : ''}{Math.round(pnlPct)}%)</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ModeStatsCard({ title, accentColor, stats, byChain }) {
  const borderColor = accentColor === 'green' ? 'border-green-500' : accentColor === 'yellow' ? 'border-yellow-500' : 'border-blue-500';
  const dotColor = accentColor === 'green' ? 'bg-green-500' : accentColor === 'yellow' ? 'bg-yellow-500' : 'bg-blue-500';
  const textAccent = accentColor === 'green' ? 'text-green-600 dark:text-green-400' : accentColor === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' : 'text-blue-600 dark:text-blue-400';

  const activeChains = byChain
    ? Object.entries(byChain).filter(([, s]) => chainActivity(s) > 0)
    : [];
  const singleChain = activeChains.length === 1 ? activeChains[0] : null;
  const singleSym = singleChain ? getNativeSymbol(singleChain[0]) : null;
  const singleStats = singleChain ? singleChain[1] : null;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 ${borderColor}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
        <h3 className={`text-sm font-bold ${textAccent}`}>{title}</h3>
      </div>
      {activeChains.length > 1 && (
        <p className="text-[10px] text-gray-400 mb-2 ml-4">P&L and size are per chain — do not add across SOL / ETH / BNB</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Open</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{stats?.active || 0}</p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500">{stats?.closed || 0} closed</p>
        </div>
        <div>
          <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">P&L</p>
          {singleStats ? (
            <>
              <p className={`text-xl font-bold ${(singleStats.totalPnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(singleStats.totalPnl || 0) >= 0 ? '+' : ''}{formatNum(singleStats.totalPnl, 3)}
                <span className="text-sm font-semibold ml-0.5">{singleSym}</span>
              </p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                {(singleStats.totalPnlPercent || 0) >= 0 ? '+' : ''}{singleStats.totalPnlPercent || 0}%
              </p>
            </>
          ) : (
            <>
              <p className="text-xl font-bold text-gray-400">—</p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500">see per chain</p>
            </>
          )}
        </div>
        <div>
          <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Win Rate</p>
          <p className={`text-xl font-bold ${(stats?.winRate || 0) >= 50 ? 'text-green-600' : 'text-orange-600'}`}>
            {stats?.winRate || 0}%
          </p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500">{stats?.wins || 0} wins</p>
        </div>
        <div>
          <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">Today</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{stats?.todayTrades || 0}</p>
          {singleStats ? (
            <p className={`text-[10px] ${(stats?.todayPnl || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {(stats?.todayPnl || 0) >= 0 ? '+' : ''}{formatNum(stats?.todayPnl, 3)} {singleSym}
            </p>
          ) : (
            <p className="text-[10px] text-gray-400">{stats?.todayTrades ? 'trades opened' : '—'}</p>
          )}
        </div>
      </div>

      <ChainBreakdown byChain={byChain} />
    </div>
  );
}






